import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { ChevronDown, ChevronUp, DollarSign, Home, Target, Landmark, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { logger } from "@/lib/logger";

interface CompSettingsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  northId: string;
  northAddress: string;
}

export const CompSettingsDialog = ({ open, onOpenChange, northId, northAddress }: CompSettingsDialogProps) => {
  const [priceSection, setPriceSection] = useState(true);
  const [detailsSection, setDetailsSection] = useState(false);
  const [locationSection, setLocationSection] = useState(false);
  const [soldSection, setSoldSection] = useState(false);
  const queryClient = useQueryClient();

  // Form state
  const [priceRange, setPriceRange] = useState(10);
  const [minBeds, setMinBeds] = useState<number | undefined>();
  const [maxBeds, setMaxBeds] = useState<number | undefined>();
  const [minBaths, setMinBaths] = useState<number | undefined>();
  const [maxBaths, setMaxBaths] = useState<number | undefined>();
  const [propertyType, setPropertyType] = useState("any");
  const [keywords, setKeywords] = useState("");
  const [radius, setRadius] = useState(10);
  const [sameCity, setSameCity] = useState(false);
  const [sameZip, setSameZip] = useState(false);
  const [sameState, setSameState] = useState(false);
  const [soldTimeframe, setSoldTimeframe] = useState("dont_include");

  const { data: settings } = useQuery({
    queryKey: ['comp-settings', northId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('comp_settings')
        .select('*')
        .eq('north_id', northId)
        .maybeSingle();

      if (error && error.code !== 'PGRST116') throw error;
      return data;
    },
    enabled: open,
  });

  useEffect(() => {
    if (settings) {
      setPriceRange(settings.price_range_percentage || 10);
      setMinBeds(settings.min_bedrooms || undefined);
      setMaxBeds(settings.max_bedrooms || undefined);
      setMinBaths(settings.min_bathrooms || undefined);
      setMaxBaths(settings.max_bathrooms || undefined);
      setKeywords(settings.keywords || "");
      setRadius(settings.radius_miles || 10);
      setSameCity(settings.same_city || false);
      setSameZip(settings.same_zip || false);
      setSameState(settings.same_state || false);
      setSoldTimeframe(settings.sold_timeframe || "dont_include");
    }
  }, [settings]);

  const saveSettings = useMutation({
    mutationFn: async () => {
      const settingsData = {
        north_id: northId,
        price_range_percentage: priceRange,
        min_bedrooms: minBeds,
        max_bedrooms: maxBeds,
        min_bathrooms: minBaths,
        max_bathrooms: maxBaths,
        keywords,
        radius_miles: radius,
        same_city: sameCity,
        same_zip: sameZip,
        same_state: sameState,
        sold_timeframe: soldTimeframe,
      };

      if (settings?.id) {
        const { error } = await supabase
          .from('comp_settings')
          .update(settingsData)
          .eq('id', settings.id);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('comp_settings')
          .insert(settingsData);
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['comp-settings', northId] });
      queryClient.invalidateQueries({ queryKey: ['comps', northId] });
      toast.success('Comp settings saved');
      onOpenChange(false);
    },
    onError: (error) => {
      logger.error('Error saving settings:', error);
      toast.error('Failed to save settings');
    },
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Comp Settings for:</DialogTitle>
          <p className="text-sm text-muted-foreground">{northAddress}</p>
        </DialogHeader>

        <div className="space-y-3">
          {/* Price Section */}
          <Collapsible open={priceSection} onOpenChange={setPriceSection}>
            <Card className="border">
              <CollapsibleTrigger className="w-full p-4 flex items-center justify-between hover:bg-muted/50">
                <div className="flex items-center gap-2">
                  <DollarSign className="h-5 w-5 text-primary" />
                  <span className="font-medium">Price</span>
                </div>
                {priceSection ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
              </CollapsibleTrigger>
              <CollapsibleContent>
                <div className="p-4 pt-0 space-y-4">
                  <p className="text-sm text-muted-foreground">
                    Choose how far above and below your list price to include comps.
                  </p>
                  <div className="flex gap-2">
                    {[5, 10, 15, 20, 25].map((pct) => (
                      <Button
                        key={pct}
                        variant={priceRange === pct ? "default" : "outline"}
                        size="sm"
                        onClick={() => setPriceRange(pct)}
                        className="flex-1"
                      >
                        {pct}%
                      </Button>
                    ))}
                  </div>
                  <div className="flex items-center gap-2">
                    <Label className="whitespace-nowrap">Custom</Label>
                    <Input
                      type="number"
                      value={priceRange}
                      onChange={(e) => setPriceRange(parseInt(e.target.value) || 10)}
                      className="w-20"
                    />
                    <span className="text-sm text-muted-foreground">%</span>
                  </div>
                </div>
              </CollapsibleContent>
            </Card>
          </Collapsible>

          {/* Listing Details Section */}
          <Collapsible open={detailsSection} onOpenChange={setDetailsSection}>
            <Card className="border">
              <CollapsibleTrigger className="w-full p-4 flex items-center justify-between hover:bg-muted/50">
                <div className="flex items-center gap-2">
                  <Home className="h-5 w-5 text-primary" />
                  <span className="font-medium">Listing Details</span>
                </div>
                {detailsSection ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
              </CollapsibleTrigger>
              <CollapsibleContent>
                <div className="p-4 pt-0 space-y-4">
                  <p className="text-sm text-muted-foreground">
                    Filter comps by beds, baths, property type, or keywords.
                  </p>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Beds</Label>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <Label className="text-xs text-muted-foreground">Min</Label>
                          <Select value={minBeds?.toString() || "any"} onValueChange={(v) => setMinBeds(v === "any" ? undefined : parseInt(v))}>
                            <SelectTrigger>
                              <SelectValue placeholder="Any" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="any">Any</SelectItem>
                              {[1, 2, 3, 4, 5].map(n => <SelectItem key={n} value={n.toString()}>{n}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label className="text-xs text-muted-foreground">Max</Label>
                          <Select value={maxBeds?.toString() || "unlimited"} onValueChange={(v) => setMaxBeds(v === "unlimited" ? undefined : parseInt(v))}>
                            <SelectTrigger>
                              <SelectValue placeholder="5+" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="unlimited">5+</SelectItem>
                              {[1, 2, 3, 4, 5].map(n => <SelectItem key={n} value={n.toString()}>{n}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label>Baths</Label>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <Label className="text-xs text-muted-foreground">Min</Label>
                          <Select value={minBaths?.toString() || "any"} onValueChange={(v) => setMinBaths(v === "any" ? undefined : parseFloat(v))}>
                            <SelectTrigger>
                              <SelectValue placeholder="Any" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="any">Any</SelectItem>
                              {[1, 2, 3, 4, 5].map(n => <SelectItem key={n} value={n.toString()}>{n}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label className="text-xs text-muted-foreground">Max</Label>
                          <Select value={maxBaths?.toString() || "unlimited"} onValueChange={(v) => setMaxBaths(v === "unlimited" ? undefined : parseFloat(v))}>
                            <SelectTrigger>
                              <SelectValue placeholder="5+" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="unlimited">5+</SelectItem>
                              {[1, 2, 3, 4, 5].map(n => <SelectItem key={n} value={n.toString()}>{n}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Property Type</Label>
                    <Select value={propertyType} onValueChange={setPropertyType}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="any">Any</SelectItem>
                        <SelectItem value="single_family">Single Family</SelectItem>
                        <SelectItem value="condo">Condo</SelectItem>
                        <SelectItem value="townhouse">Townhouse</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Contains Keywords</Label>
                    <p className="text-xs text-muted-foreground">You can add 1 keyword per North.</p>
                    <Input
                      placeholder="Keywords"
                      value={keywords}
                      onChange={(e) => setKeywords(e.target.value)}
                    />
                  </div>
                </div>
              </CollapsibleContent>
            </Card>
          </Collapsible>

          {/* Location Section */}
          <Collapsible open={locationSection} onOpenChange={setLocationSection}>
            <Card className="border">
              <CollapsibleTrigger className="w-full p-4 flex items-center justify-between hover:bg-muted/50">
                <div className="flex items-center gap-2">
                  <Target className="h-5 w-5 text-primary" />
                  <span className="font-medium">Location</span>
                </div>
                {locationSection ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
              </CollapsibleTrigger>
              <CollapsibleContent>
                <div className="p-4 pt-0 space-y-4">
                  <p className="text-sm text-muted-foreground">
                    Set how far out from your listing North should search for comps.
                  </p>
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      value={radius}
                      onChange={(e) => setRadius(parseInt(e.target.value) || 10)}
                      className="w-20"
                    />
                    <span className="text-sm text-muted-foreground">Miles</span>
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <Label>Same State</Label>
                      <Switch checked={sameState} onCheckedChange={setSameState} />
                    </div>
                    <div className="flex items-center justify-between">
                      <Label>Same City</Label>
                      <Switch checked={sameCity} onCheckedChange={setSameCity} />
                    </div>
                    <div className="flex items-center justify-between">
                      <Label>Same Zip Code</Label>
                      <Switch checked={sameZip} onCheckedChange={setSameZip} />
                    </div>
                  </div>
                </div>
              </CollapsibleContent>
            </Card>
          </Collapsible>

          {/* Sold Section */}
          <Collapsible open={soldSection} onOpenChange={setSoldSection}>
            <Card className="border">
              <CollapsibleTrigger className="w-full p-4 flex items-center justify-between hover:bg-muted/50">
                <div className="flex items-center gap-2">
                  <Landmark className="h-5 w-5 text-primary" />
                  <span className="font-medium">Sold</span>
                </div>
                {soldSection ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
              </CollapsibleTrigger>
              <CollapsibleContent>
                <div className="p-4 pt-0 space-y-4">
                  <p className="text-sm text-muted-foreground">
                    Choose how far back North should look for sold listings.
                  </p>
                  <div className="space-y-2">
                    <Label>Timeframe</Label>
                    <Select value={soldTimeframe} onValueChange={setSoldTimeframe}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="dont_include">Don't include solds</SelectItem>
                        <SelectItem value="3_months">Last 3 months</SelectItem>
                        <SelectItem value="6_months">Last 6 months</SelectItem>
                        <SelectItem value="12_months">Last 12 months</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CollapsibleContent>
            </Card>
          </Collapsible>

          {/* Warning */}
          <Card className="bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800">
            <div className="p-4 flex gap-3">
              <AlertTriangle className="h-5 w-5 text-yellow-600 dark:text-yellow-500 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="font-medium text-yellow-900 dark:text-yellow-100">Heads up!</h4>
                <p className="text-sm text-yellow-800 dark:text-yellow-200">
                  Any updates you make will apply only to this North.
                </p>
              </div>
            </div>
          </Card>

          <div className="flex justify-end">
            <Button onClick={() => saveSettings.mutate()} disabled={saveSettings.isPending}>
              {saveSettings.isPending ? 'Saving...' : 'Save'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
